import { useEffect, useState } from "react";
import { useRef, useLayoutEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import {
  format,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  differenceInMinutes,
} from "date-fns";
import ParticipantSelector from "../components/expenses/ParticipantSelector";
import ExpenseList from "../components/expenses/ExpenseList";
import ExpenseSummary from "../components/expenses/ExpenseSummary";
import PaymentForm from "../components/expenses/PaymentForm";
import NewExpenseBottomSheet from "../components/expenses/NewExpenseBottomSheet";
import TripTabs, { TabKey } from "../components/TripTabs";
import TripDocuments from "../components/TripDocuments";
import { useNavigate } from "react-router-dom";
import SelfParticipationCheckbox from "../components/SelfParticipationCheckbox";
import ParticipantCard from "../components/participants/ParticipantCard";
import { useRealtimeChannel } from "../lib/useRealtimeChannel";
import { useTripRealtimeSync } from "../lib/useTripRealtimeSync";
import { AddTodoModal, useAddTodoModal } from "../components/AddTodoModal";
import type { Participant } from "../components/expenses/ParticipantSelector";
import TodoList from "../components/TodoList";
import TodoStatusPanel from "../components/TodoStatusPanel";
import UserPacklistTab from "../components/UserPacklistTab";
import FloatingActionButton from "../components/FloatingActionButton";
import PhaseContextCard from "../components/PhaseContextCard";
import ActivityFeed from "../components/ActivityFeed";
import TripMap from "../components/TripMap";
import InviteUserForm from "../components/InviteUserForm";
import { Share2 } from "lucide-react";
import { filterProfileForViewer } from "../utils/visibility";
import TripSuggestions from "../components/TripSuggestions";
import NewSuggestionModal from "../components/NewSuggestionModal";
import CreateNotificationModal from "../components/CreateNotificationModal";
import { useLocation } from "react-router-dom";
import DestinationSearchInput from "../components/DestinationSearchInput";
import PollEditorModal from "../components/PollEditorModal";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import AddVirtualParticipantModal from "../components/participants/AddVirtualParticipantModal";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";
import VerticalTimeline from "../components/VerticalTimeline";
import AiTripPlanningModal from "../components/AiTripPlanningModal";
import CurrentQuiz from "../components/CurrentQuiz";

import type {
  EnrichedParticipant,
  VisibleProfile,
} from "../types/participants";

interface Trip {
  id: string;
  name: string;
  description: string;
  destination: string;
  start_date: string;
  end_date: string;
  is_public: boolean;
  max_participants: number | null;
  cover_image_url?: string;
  creator_id: string;
  join_code: string;
  quiz_enabled?: boolean;
}

interface ProgramItem {
  id: string;
  title: string;
  description: string;
  category: string;
  start_time: string | null;
  end_time: string | null;
  scheduled_date?: string | null;
  from_suggestion_id?: string | null;
  creator_id: string;
  location?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
}

interface TripSuggestionsProps {
  tripId: string;
  userId: string;
}

function getSortedProgramItems(items: ProgramItem[]): ProgramItem[] {
  return [...items].sort((a, b) => {
    const aTime = a.start_time ? new Date(a.start_time).getTime() : a.scheduled_date ? new Date(a.scheduled_date).getTime() : Infinity;
    const bTime = b.start_time ? new Date(b.start_time).getTime() : b.scheduled_date ? new Date(b.scheduled_date).getTime() : Infinity;
    return aTime - bTime;
  });
}

function getGoogleMapsUrl(item: ProgramItem): string | null {
  if (item.location_lat && item.location_lng) {
    return `https://www.google.com/maps/search/?api=1&query=${item.location_lat},${item.location_lng}`;
  }
  if (item.location) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      item.location
    )}`;
  }
  return null;
}
export type { ProgramItem };

export default function TripDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [participants, setParticipants] = useState<EnrichedParticipant[]>([]);
  const [virtualParticipants, setVirtualParticipants] = useState<any[]>([]);
  const isOrganizer = (): boolean => {
    if (!user || !trip) return false;
    if (user.id === trip.creator_id) return true;
    return isCoOrganizer;
  };
  const [pendingRequests, setPendingRequests] = useState<EnrichedParticipant[]>(
    []
  );
  const [expandedParticipantId, setExpandedParticipantId] = useState<
    string | null
  >(null);
  const [showModerationPanel, setShowModerationPanel] = useState(true);
  const blockedCount = participants.filter((p) => p.is_blocked).length;
  const pendingCount = pendingRequests.length;
  const [programItems, setProgramItems] = useState<ProgramItem[]>([]);

  const [showAddVirtualModal, setShowAddVirtualModal] = useState(false);
  const [paymentPrefill, setPaymentPrefill] = useState<{ userId: string; amount: number } | null>(null);
  const [showExpenseBottomSheet, setShowExpenseBottomSheet] = useState(false);

  const { showConfirm } = useConfirmDialog();

  function setSortedProgramItems(items: ProgramItem[]) {
    setProgramItems(getSortedProgramItems(items));
  }

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const [modalOpen, setModalOpen] = useState(false); // Programmpunkt-Modal
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false); // Vorschlags-Modal

  const [selectedItem, setSelectedItem] = useState<ProgramItem | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Reisen");
  const [schedulingMode, setSchedulingMode] = useState<"none" | "date" | "datetime">("datetime");
  const [scheduledDate, setScheduledDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [locationText, setLocationText] = useState("");
  const [locationCoords, setLocationCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeTimelineDay, setActiveTimelineDay] = useState<string | null>(
    null
  );

  const [saving, setSaving] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    time?: string;
    timeOrder?: string;
  }>({});
  const [isCoOrganizer, setIsCoOrganizer] = useState(false);

  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("Reisen");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [payerId, setPayerId] = useState(user?.id ?? "");
  const [sharedWith, setSharedWith] = useState<string[]>([]); // user_ids
  const [savingExpense, setSavingExpense] = useState(false);
  const formattedAmount = parseFloat(expenseAmount).toFixed(2);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const { open, openModal, closeModal } = useAddTodoModal();

  const [distributeToAll, setDistributeToAll] = useState(true);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );
  const [expenseRefreshTrigger, setExpenseRefreshTrigger] = useState(0);
  const [refreshExpenses, setRefreshExpenses] = useState(0);

  const tripStartISO = trip
    ? new Date(trip.start_date).toISOString().slice(0, 16)
    : "";
  const tripEndISO = trip
    ? new Date(new Date(trip.end_date).setHours(23, 59))
        .toISOString()
        .slice(0, 16)
    : "";
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<any[]>([]);

  const loadPendingRequests = async (tripId: string) => {
    const { data: requests, error } = await supabase
      .from("trip_participant_requests")
      .select("user_id, profiles:user_id(*)") // ✅ Join über user_id → profiles.*
      .eq("trip_id", tripId)
      .eq("status", "pending");

    if (error) {
      console.error(
        "❌ Fehler beim Laden der offenen Anfragen:",
        error.message
      );
      return;
    }

    const enriched: EnrichedParticipant[] = (requests || [])
      .map((r) => {
        const profile = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;

        if (!profile) return null;

        const filtered = filterProfileForViewer(
          profile,
          user?.id ?? "",
          isOrganizer()
        );

        return {
          ...filtered,
          trip_id: tripId, // 🔧 unbedingt ergänzen
          is_co_organizer: false,
          is_blocked: false,
        };
      })
      .filter((p): p is EnrichedParticipant => p !== null);

    setPendingRequests(enriched);
  };
  const [suggestionSaved, setSuggestionSaved] = useState(false);

  // Aufruf für aufgeschaltene Funktionen
  const pollFeatureActive = useFeatureAccess("trip_program_poll");
  const quizFeatureActive = useFeatureAccess("ai_quiz_game");
  const canAddVirtual = useFeatureAccess("virtual_participants");
  const aiPlanningActive = useFeatureAccess("ai_trip_planning");
  const [showAiPlanningModal, setShowAiPlanningModal] = useState(false);

  const [updatingQuiz, setUpdatingQuiz] = useState(false);
  const [pendingQuizEnabled, setPendingQuizEnabled] = useState<boolean | null>(
    null
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab") ?? "overview";
  const addParam = searchParams.get("add");
  const showAddTaskModal = addParam === "task";
  const showAddNotificationModal = addParam === "notification";
  const showAddSuggestionModal = addParam === "suggestion";
  const showAddProgramModal = searchParams.get("add") === "new";
  const showAddExpenseModal = searchParams.get("add") === "expense";
  const showAddPaymentModal = searchParams.get("add") === "payment";

  const [hasPollOptions, setHasPollOptions] = useState(false);
  const [pollEditorOpen, setPollEditorOpen] = useState(false);
  const [newProgramItemId, setNewProgramItemId] = useState<string | null>(null);

  useEffect(() => {
    if (tabParam !== activeTab) {
      setActiveTab(tabParam as typeof activeTab);
    }
  }, [tabParam]);

  useEffect(() => {
    if (showAddProgramModal) {
      setSelectedItem(null);
      setTitle("");
      setDescription("");
      setCategory("Reisen");
      setSchedulingMode("datetime");

      if (trip) {
        const start = new Date(trip.start_date + "T08:00");
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        setScheduledDate(trip.start_date);
        setStartTime(start.toISOString().slice(0, 16));
        setEndTime(end.toISOString().slice(0, 16));
      }

      setModalOpen(true);
    }
  }, [showAddProgramModal, trip]);

  useEffect(() => {
    if (showAddTaskModal) {
      openModal(); // bereits vorhanden von useAddTodoModal
    }
  }, [showAddTaskModal]);

  useEffect(() => {
    if (showAddNotificationModal) {
      setShowNotificationModal(true);
    }
  }, [showAddNotificationModal]);

  useEffect(() => {
    if (showAddSuggestionModal) {
      setSuggestionModalOpen(true);
    }
  }, [showAddSuggestionModal]);

  useEffect(() => {
    if (!trip || !user || !trip.quiz_enabled) return;

    const triggerQuiz = async () => {
      await supabase.functions.invoke("generate-quiz-for-trip", {
        body: {
          trip_id: trip.id,
          user_id: user.id,
          ignore_interval: false,
        },
      });
    };

    triggerQuiz(); // nur auslösen, wenn nötig
  }, [trip?.id, user?.id]);

  // Realtime-Kanal zur sofortigen Reaktion auf Blockierungen
  useEffect(() => {
    if (!trip?.id || !user?.id) return;

    const channel = supabase
      .channel("block-check")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "trip_participants",
          filter: `trip_id=eq.${trip.id}`,
        },
        (payload) => {
          const updated = payload.new as EnrichedParticipant;
          if (updated.user_id === user.id && updated.is_blocked) {
            alert("⛔ Du wurdest von dieser Reise blockiert.");
            navigate("/dashboard", { replace: true });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [trip?.id, user?.id]);

  // Realtime-Kanal zur sofortigen Reaktion auf Anfragen
  useEffect(() => {
    if (!trip?.id || !user?.id) return;

    const channel = supabase
      .channel("participation-request")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "trip_participant_requests",
          filter: `trip_id=eq.${trip.id}`,
        },
        async (payload) => {
          const newRequest = payload.new;

          // Sicherheits-Check auf Status 'pending'
          if (newRequest.status === "pending") {
            await loadPendingRequests(trip.id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [trip?.id, user?.id]);

  useEffect(() => {
    const fetchVirtualParticipants = async () => {
      if (!trip?.id) return; // 💡 Früher Ausstieg, falls trip null

      const { data, error } = await supabase
        .from("trip_participants")
        .select("virtual_profile_id, virtual_profiles(*)")
        .eq("trip_id", trip.id)
        .not("virtual_profile_id", "is", null);

      if (error) {
        console.error("Fehler beim Laden virtueller Teilnehmer:", error);
      } else {
        const enrichedVirtuals = data.map((entry) => ({
          ...entry.virtual_profiles,
          virtual_profile_id: entry.virtual_profile_id,
        }));

        setVirtualParticipants(enrichedVirtuals);
      }
    };

    fetchVirtualParticipants(); // keine zusätzliche trip.id-Prüfung hier nötig
  }, [trip?.id]);

  useTripRealtimeSync<any>({
    tripId: trip?.id ?? null,
    table: "trip_documents",
    setItems: setDocuments,
    getId: (item) => item.id,
    transform: (docs) =>
      [...docs].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
  });

  useTripRealtimeSync<ProgramItem>({
    tripId: trip?.id ?? null,
    table: "trip_program_items",
    setItems: setProgramItems,
    getId: (item) => item.id,
    transform: getSortedProgramItems, // 🆕 automatisches Sortieren
  });

  useTripRealtimeSync<any>({
    tripId: trip?.id ?? null,
    table: "trip_expenses",
    setItems: () => setRefreshExpenses((prev) => prev + 1),
    getId: (item) => item.id,
  });

  useTripRealtimeSync<any>({
    tripId: trip?.id ?? null,
    table: "trip_payments",
    setItems: () => setRefreshExpenses((prev) => prev + 1),
    getId: (item) => item.id,
  });

  useTripRealtimeSync<EnrichedParticipant>({
    tripId: trip?.id ?? null,
    table: "trip_participants",
    getId: (item) => item.user_id,
    setItems: async (updater) => {
      if (trip?.id) {
        await refreshParticipants(trip.id);
      }

      // 💥 Wenn aktueller User geblockt wurde → rauswerfen
      if (
        user &&
        typeof updater === "object" &&
        updater !== null &&
        "user_id" in updater &&
        "is_blocked" in updater
      ) {
        const maybeUserId = (updater as any).user_id;
        const maybeIsBlocked = (updater as any).is_blocked;

        if (maybeUserId === user.id && maybeIsBlocked === true) {
          alert("⛔ Du wurdest für diese Reise blockiert.");
          navigate("/dashboard", { replace: true });
        }
      }
    },
  });

  useTripRealtimeSync<any>({
    tripId: trip?.id ?? null,
    table: "profiles",
    getId: (item) => item.id,
    setItems: async () => {
      // Teilnehmer neu laden (inkl. Profile)
      if (trip?.id) {
        await refreshParticipants(trip.id);
      }
    },
  });

  useEffect(() => {
    const lastExpandedDay = Object.entries(expandedDays).find(
      ([_, expanded]) => expanded
    );

    if (lastExpandedDay) {
      setActiveTimelineDay(lastExpandedDay[0]);
    }
  }, [expandedDays]);

  //Zum Testen
  useEffect(() => {
    if (id && user) {
      fetchTrip();
    }
  }, [id, user]);

  const refreshParticipants = async (tripId: string = trip?.id!) => {
    if (!user) return;

    const { data: participantData, error: participantError } = await supabase
      .from("trip_participants")
      .select("user_id, is_co_organizer, is_blocked")
      .eq("trip_id", tripId);

    if (participantError) {
      console.error(
        "❌ Fehler beim Nachladen der Teilnehmer:",
        participantError.message
      );
      return;
    }

    const { data: profilesData, error: profilesError } = await supabase
      .from("safe_profiles")
      .select("*")
      .eq("trip_id", tripId);

    if (profilesError) {
      console.error("❌ Fehler beim Laden der Profile:", profilesError.message);
      return;
    }

    const enriched = participantData
      .map((p) => {
        const profile = profilesData.find(
          (prof) => String(prof.user_id) === String(p.user_id)
        );

        if (!profile) return null;

        return {
          ...profile,
          first_name: profile.first_name ?? "",
          last_name: profile.last_name ?? "",
          email: profile.email ?? "",
          profile_picture_url: profile.profile_picture_url ?? "",
          emergency_contact_name: profile.emergency_contact_name ?? "",
          emergency_contact_phone: profile.emergency_contact_phone ?? "",
          allergies: profile.allergies ?? "",
          medications: profile.medications ?? "",
          notes: profile.notes ?? "",
          diet: profile.diet ?? "",
          gender: profile.gender ?? "",
          clothing_size: profile.clothing_size ?? "",
          languages: profile.languages ?? [],
          licenses: profile.licenses ?? [],
          notification_preference: profile.notification_preference ?? [],
          can_swim: profile.can_swim,
          has_insurance: profile.has_insurance,

          // falls gebraucht:
          is_co_organizer: p.is_co_organizer ?? false,
          is_blocked: p.is_blocked ?? false,
        } as EnrichedParticipant;
      })
      .filter((p): p is EnrichedParticipant => p !== null);

    setParticipants(enriched);

    // 🔄 Virtuelle Teilnehmer separat nachladen
    const { data: virtualData, error: virtualError } = await supabase
      .from("trip_participants")
      .select("virtual_profile_id")
      .eq("trip_id", tripId)
      .is("user_id", null);

    if (virtualError) {
      console.error(
        "❌ Fehler beim Laden virtueller Teilnehmer:",
        virtualError.message
      );
      return;
    }

    const ids = virtualData.map((v) => v.virtual_profile_id).filter((id): id is string => id !== null);

    if (ids.length === 0) {
      setVirtualParticipants([]);
      return;
    }

    const { data: virtualProfiles, error: virtualProfilesError } =
      await supabase.from("virtual_profiles").select("*").in("id", ids);

    if (virtualProfilesError) {
      console.error(
        "❌ Fehler beim Laden der virtuellen Profile:",
        virtualProfilesError.message
      );
      return;
    }

    const enrichedVirtuals = virtualProfiles.map((profile) => ({
      virtual_profile_id: profile.id,
      email: profile.email ?? "",
      first_name: profile.first_name ?? "",
      last_name: profile.last_name ?? "",
    }));

    setVirtualParticipants(enrichedVirtuals);
  };

  const loadProgramItems = async (tripId: string) => {
    const { data, error } = await supabase
      .from("trip_program_items")
      .select("*")
      .eq("trip_id", tripId)
      .order("scheduled_date", { ascending: true, nullsFirst: false })
      .order("start_time", { ascending: true, nullsFirst: false });

    if (error) {
      console.error("❌ Fehler beim Laden der Programmpunkte:", error.message);
      return;
    }

    setProgramItems((data || []) as ProgramItem[]);
  };

  const handleMoveItem = async (itemId: string, newDate: string) => {
    const item = programItems.find((i) => i.id === itemId);
    if (!item) return;

    const [y, m, d] = newDate.split("-").map(Number);

    // Optimistic Update
    setProgramItems((prev) =>
      prev.map((i) => {
        if (i.id !== itemId) return i;
        if (i.start_time) {
          const newStart = new Date(i.start_time);
          newStart.setFullYear(y, m - 1, d);
          const newEnd = i.end_time ? new Date(i.end_time) : null;
          if (newEnd) newEnd.setFullYear(y, m - 1, d);
          return {
            ...i,
            start_time: newStart.toISOString(),
            end_time: newEnd?.toISOString() ?? i.end_time,
            scheduled_date: newDate,
          };
        }
        return { ...i, scheduled_date: newDate };
      })
    );

    // DB-Update
    const updates: Record<string, string> = {};
    if (item.start_time) {
      const newStart = new Date(item.start_time);
      newStart.setFullYear(y, m - 1, d);
      updates.start_time = newStart.toISOString();
      if (item.end_time) {
        const newEnd = new Date(item.end_time);
        newEnd.setFullYear(y, m - 1, d);
        updates.end_time = newEnd.toISOString();
      }
      updates.scheduled_date = newDate;
    } else {
      updates.scheduled_date = newDate;
    }

    const { error } = await supabase
      .from("trip_program_items")
      .update(updates)
      .eq("id", itemId);

    if (error) {
      console.error("❌ Fehler beim Verschieben:", error);
      if (trip) loadProgramItems(trip.id); // Rollback
    }
  };

  const loadDocuments = async (tripId: string) => {
    const { data, error } = await supabase
      .from("trip_documents")
      .select("*")
      .eq("trip_id", tripId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Fehler beim Laden der Dokumente:", error.message);
      return;
    }

    setDocuments(data || []);
  };

  const fetchTrip = async () => {
    const { data, error } = await supabase
      .from("trips")
      .select("*")
      .eq("id", id!)
      .single();

    if (error || !data) {
      console.error("❌ Fehler beim Laden der Reise:", error?.message);
      setLoading(false);
      return;
    }

    setTrip(data as Trip);

    // 👤 Teilnehmerrolle prüfen, wenn nicht Creator
    if (user?.id && data.creator_id !== user.id) {
      const { data: participantData } = await supabase
        .from("trip_participants")
        .select("is_co_organizer, is_blocked")
        .eq("trip_id", data.id)
        .eq("user_id", user.id)
        .single();

      if (participantData?.is_blocked === true) {
        alert("⛔ Du wurdest für diese Reise blockiert.");
        navigate("/dashboard", { replace: true });
        return;
      }

      const isCo = participantData?.is_co_organizer === true;
      setIsCoOrganizer(isCo);

      await refreshParticipants(data.id);
      await loadProgramItems(data.id);
      await loadDocuments(data.id);

      if (isCo) {
        await loadPendingRequests(data.id);
      }

      setLoading(false);
      return;
    }

    // ✅ Falls Creator
    if (user?.id === data.creator_id) {
      await refreshParticipants(data.id);
      await loadProgramItems(data.id);
      await loadDocuments(data.id);
      await loadPendingRequests(data.id); // ✅ für Creator
      setLoading(false);
      return;
    }
  };

  const handleRequestAction = async (
    userId: string,
    action: "accepted" | "rejected"
  ) => {
    if (!trip) return;

    if (action === "accepted") {
      // 1. Als normaler Teilnehmer hinzufügen
      const { error: insertError } = await supabase
        .from("trip_participants")
        .insert({
          trip_id: trip.id,
          user_id: userId,
          is_co_organizer: false,
          is_blocked: false,
        });

      if (insertError) {
        console.error("❌ Fehler beim Hinzufügen:", insertError.message);
        return;
      }
    } else if (action === "rejected") {
      // ⛔ Blockierten Eintrag erstellen
      const { error: blockInsertError } = await supabase
        .from("trip_participants")
        .insert({
          trip_id: trip.id,
          user_id: userId,
          is_co_organizer: false,
          is_blocked: true,
        });

      if (blockInsertError) {
        console.error("❌ Fehler beim Blockieren:", blockInsertError.message);
        return;
      }
    }

    // 2. Anfrage löschen (egal ob accepted oder rejected)
    const { error: deleteError } = await supabase
      .from("trip_participant_requests")
      .delete()
      .eq("trip_id", trip.id)
      .eq("user_id", userId);

    if (deleteError) {
      console.error("❌ Fehler beim Löschen der Anfrage:", deleteError.message);
      return;
    }

    // 3. Neu laden
    await refreshParticipants(trip.id);
    await loadPendingRequests(trip.id);
  };

  function isWithinTripDates(startTime: string, endTime: string): boolean {
    if (!trip) return false;

    const start = new Date(startTime);
    const end = new Date(endTime);

    const tripStart = new Date(trip.start_date);
    const tripEnd = new Date(trip.end_date);

    // Optional: Grenzzeit auf 00:00 Uhr setzen
    tripStart.setHours(0, 0, 0, 0);
    tripEnd.setHours(23, 59, 59, 999);

    return start >= tripStart && end <= tripEnd;
  }

  const handleSave = async () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = "Titel ist erforderlich.";
    }

    /*
    if (!description.trim()) {
      newErrors.description = "Beschreibung ist erforderlich.";
    }
      */

    if (schedulingMode === "date" && !scheduledDate) {
      newErrors.time = "Bitte ein Datum wählen.";
    } else if (schedulingMode === "datetime") {
      if (!startTime || !endTime) {
        newErrors.time = "Start- und Endzeit müssen angegeben werden.";
      } else {
        const start = new Date(startTime);
        const end = new Date(endTime);

        if (start.getTime() >= end.getTime()) {
          newErrors.timeOrder = "Startzeit muss vor der Endzeit liegen.";
        }

        const tripStart = new Date(trip!.start_date);
        const tripEnd = new Date(trip!.end_date);
        tripStart.setHours(0, 0, 0, 0);
        tripEnd.setHours(23, 59, 59, 999);

        if (start < tripStart || end > tripEnd) {
          newErrors.time = "Datum muss innerhalb des Reisezeitraums liegen.";
        }
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return; // Abbrechen bei Fehlern
    }

    setSaving(true);

    const timePayload = {
      scheduled_date: schedulingMode === "none" ? null : scheduledDate || null,
      start_time: schedulingMode === "datetime" ? new Date(startTime).toISOString() : null,
      end_time: schedulingMode === "datetime" ? new Date(endTime).toISOString() : null,
    };

    if (selectedItem) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: updatedData, error } = await (supabase as any)
        .from("trip_program_items")
        .update({
          title,
          description,
          category,
          ...timePayload,
          location: locationText || null,
          location_lat: locationCoords?.lat ?? null,
          location_lng: locationCoords?.lng ?? null,
        })
        .eq("id", selectedItem.id)
        .select();

      if (!error && updatedData && updatedData.length > 0) {
        const updatedItem = updatedData[0];
        const updated = programItems.map((p) =>
          p.id === selectedItem.id ? updatedItem as ProgramItem : p
        );
        setProgramItems(updated);

        if (hasPollOptions) {
          setNewProgramItemId(updatedItem.id);
          setPollEditorOpen(true);
        }
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error, data } = await (supabase as any)
        .from("trip_program_items")
        .insert({
          trip_id: trip!.id,
          title,
          description,
          category,
          ...timePayload,
          creator_id: user!.id,
          location: locationText || null,
          location_lat: locationCoords?.lat ?? null,
          location_lng: locationCoords?.lng ?? null,
        })
        .select();

      if (!error) {
        setProgramItems((prev) => [...prev, ...((data || []) as ProgramItem[])]);

        if (hasPollOptions) {
          setNewProgramItemId(data[0].id);
          setPollEditorOpen(true);
        }
      }
    }

    setModalOpen(false);
    setSelectedItem(null);
    setTitle("");
    setDescription("");
    setCategory("Reisen");
    setSchedulingMode("datetime");
    setScheduledDate("");
    setStartTime("");
    setEndTime("");
    setSaving(false);
  };

  const handleSaveExpense = async () => {
    if (!isOrganizer()) {
      alert("Du hast keine Berechtigung, Ausgaben zu erfassen.");
      return;
    }

    if (!isOrganizer()) {
      alert("Du hast keine Berechtigung, Änderungen vorzunehmen.");
      return;
    }

    if (!trip || !user || !expenseTitle || !expenseAmount) {
      return;
    }

    const total = parseFloat(expenseAmount);
    if (payerId === "reisekasse") {
      // Lade Einzahlungen (ohne user_id) = allgemeine Zahlungen
      const { data: paymentsData } = await supabase
        .from("trip_payments")
        .select("*")
        .eq("trip_id", trip.id);

      // Lade Ausgaben (ohne payer_id) = aus Reisekasse bezahlt
      const { data: expensesData } = await supabase
        .from("trip_expenses")
        .select("*")
        .eq("trip_id", trip.id);

      const zahlungen = paymentsData?.filter((p) => !p.user_id) || [];
      const ausgaben = expensesData?.filter((e) => !e.payer_id) || [];

      const saldoReisekasse =
        zahlungen.reduce((sum, p) => sum + p.amount, 0) -
        ausgaben.reduce((sum, e) => sum + e.amount, 0);

      if (total > saldoReisekasse) {
        alert(
          "⚠️ Die Reisekasse hat nicht genügend Guthaben für diese Ausgabe."
        );
        return;
      }
    }

    if (isNaN(total) || total <= 0) return;

    const involved = distributeToAll
      ? participants.filter((p) => !p.is_blocked).map((p) => p.user_id)
      : selectedParticipants;

    if (involved.length === 0) return;

    const sharePerUser = parseFloat((total / involved.length).toFixed(2));
    // Falls Zahler selbst betroffen ist, bleibt sein Anteil bestehen
    // Falls nicht, bekommt er den vollen Betrag als Gutschrift, zahlt aber keinen Anteil

    setSavingExpense(true);

    // ➕ INSERT in trip_expenses
    const { data: expenseData, error: expenseError } = await supabase
      .from("trip_expenses")
      .insert({
        trip_id: trip.id,
        title: expenseTitle,
        category: expenseCategory,
        amount: total,
        payer_id: payerId === "reisekasse" ? null : payerId,
        creator_id: user.id,
      })
      .select();

    if (expenseError || !expenseData?.[0]) {
      console.error(
        "❌ Fehler beim Speichern der Ausgabe:",
        expenseError?.message
      );
      setSavingExpense(false);
      setExpenseRefreshTrigger((prev) => prev + 1); // Trigger für ExpenseList

      return;
    }

    const expenseId = expenseData[0].id;

    // ➕ INSERT in trip_expense_shares
    const isPayerInvolved = involved.includes(payerId);
    const individualShare = parseFloat((total / involved.length).toFixed(2));

    const shares = involved.map((user_id) => ({
      expense_id: expenseId,
      user_id,
      amount: individualShare,
      is_paid: false,
    }));

    // Falls der Zahler nicht betroffen ist, fügen wir keinen eigenen Anteil hinzu
    // (Er bekommt sowieso den ganzen Betrag über "Gutschrift" – also "paid" angerechnet)

    const { error: shareError } = await supabase
      .from("trip_expense_shares")
      .insert(shares);

    if (shareError) {
      console.error(
        "❌ Fehler beim Speichern der Verteilung:",
        shareError.message
      );
    } else {
      // TODO: später Liste aktualisieren
    }

    // Reset Formular
    setExpenseTitle("");
    setExpenseAmount("");
    setExpenseCategory("Reisen");
    setDistributeToAll(true);
    setSelectedParticipants([]);
    setRefreshExpenses((prev) => prev + 1); // triggert Re-Render
    setExpenseModalOpen(false);
    setSavingExpense(false);
  };

  const handleDelete = async () => {
    if (!isOrganizer()) {
      alert("Du hast keine Berechtigung, Programmpunkte zu löschen.");
      return;
    }

    if (!selectedItem) return;

    const { data: deleted, error } = await supabase
      .from("trip_program_items")
      .delete()
      .eq("id", selectedItem.id)
      .select();

    if (!error && deleted && deleted.length > 0) {
      setProgramItems((prev) => prev.filter((p) => p.id !== selectedItem.id));
      setModalOpen(false);
      setSelectedItem(null);
    } else {
      console.error("⚠️ Programmpunkt konnte nicht gelöscht werden.");
    }

    if (!error) {
      setProgramItems((prev) => prev.filter((p) => p.id !== selectedItem.id));
      setModalOpen(false);
      setSelectedItem(null);
    }
  };

  const renderTimeline = () => {
    if (!trip) return null;

    const days = eachDayOfInterval({
      start: new Date(trip.start_date),
      end: new Date(trip.end_date),
    });

    const toggleAll = (expand: boolean) => {
      const newState: Record<string, boolean> = {};
      days.forEach((d) => {
        newState[d.toISOString()] = expand;
      });
      setExpandedDays(newState);
    };

    const participantsForTodoModal: Participant[] = participants
      .filter((p) => !p.is_blocked) // ❗ blockierte herausfiltern
      .map((p) => ({
        user_id: p.user_id,
        first_name: p.first_name,
        last_name: p.last_name,
        email: p.email,
      }));

    function calculateColumns(items: ProgramItem[]) {
      const columns: ProgramItem[][] = [];
      const timedItems = items.filter((i) => !!i.start_time && !!i.end_time);

      timedItems.forEach((item) => {
        const itemStart = new Date(item.start_time!).getTime();
        const itemEnd = new Date(item.end_time!).getTime();
        let placed = false;

        for (const column of columns) {
          const last = column[column.length - 1];
          const lastEnd = new Date(last.end_time!).getTime();

          if (itemStart >= lastEnd) {
            column.push(item);
            placed = true;
            break;
          }
        }

        if (!placed) {
          columns.push([item]);
        }
      });

      const map = new Map<string, { index: number; total: number }>();

      columns.forEach((col, colIndex) => {
        col.forEach((item) => {
          map.set(item.id, { index: colIndex, total: columns.length });
        });
      });

      return map;
    }

    return (
      <div className="mt-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">📆 Programm</h3>
          <div className="space-x-2 text-sm">
            <button
              onClick={() => toggleAll(true)}
              className="text-sky-400 hover:underline"
            >
              Alle öffnen
            </button>
            <button
              onClick={() => toggleAll(false)}
              className="text-sky-400 hover:underline"
            >
              Alle schließen
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {days.map((day) => {
            const dayKey = day.toISOString();
            const isPast = new Date(day) < startOfDay(new Date());
            const isExpanded = expandedDays[dayKey] ?? false;

            const toggleExpanded = () =>
              setExpandedDays((prev) => ({ ...prev, [dayKey]: !isExpanded }));

            const dayStart = startOfDay(day);
            const dayEnd = endOfDay(day);
            const items = programItems.filter((item) => {
              if (!item.start_time) return false;
              const itemStart = new Date(item.start_time);
              return itemStart >= dayStart && itemStart <= dayEnd;
            });
            const columnMap = calculateColumns(items);
            const defaultStartMin = 7 * 60; // frühester Start: 07:00
            const defaultEndMin = 22 * 60; // spätester Default: 22:00

            const eventStartTimes = items.map((i) => {
              const d = new Date(i.start_time!);
              return d.getHours() * 60 + d.getMinutes();
            });
            const eventEndTimes = items.map((i) => {
              const d = new Date(i.end_time!);
              return d.getHours() * 60 + d.getMinutes();
            });

            const minStart =
              eventStartTimes.length > 0
                ? Math.min(...eventStartTimes)
                : defaultStartMin;
            const maxEnd =
              eventEndTimes.length > 0
                ? Math.max(...eventEndTimes)
                : defaultEndMin;

            const timelineOffset = minStart;
            const minBoxHeight = 120; // px
            const pxPerMinute = 1;

            const neededHeights = items.map((item) => {
              const start = new Date(item.start_time!);
              const end = new Date(item.end_time!);
              const durationMinutes = differenceInMinutes(end, start);
              return Math.max(durationMinutes * pxPerMinute, minBoxHeight);
            });

            const totalTimelineHeight = Math.max(
              Math.max(...neededHeights, 0),
              (maxEnd - minStart) * pxPerMinute
            );

            return (
              <div key={dayKey} className="border rounded overflow-hidden">
                <button
                  onClick={toggleExpanded}
                  className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-medium border-b"
                >
                  {format(day, "EEEE, dd.MM.yyyy")} {isExpanded ? "▲" : "▼"}
                </button>
                {isExpanded &&
                  (items.length > 0 ? (
                    <div
                      id={`timeline-${dayKey}`}
                      className="relative bg-white pl-[56px] border-l-2 border-gray-300 mt-4"
                      style={{ height: `${totalTimelineHeight}px` }}
                    >
                      {/* Timeline */}
                      {Array.from(
                        { length: (maxEnd - minStart) / 60 + 1 },
                        (_, i) => {
                          const hour = Math.floor((minStart + i * 60) / 60);
                          return (
                            <div
                              key={hour}
                              className="absolute text-gray-400 text-[10px]"
                              style={{
                                top: `${i * 60}px`,
                                left: 0,
                                width: "56px",
                              }}
                            >
                              <div className="border-t border-dashed border-gray-200 w-full" />
                              <span className="absolute left-1 -mt-2">
                                {hour.toString().padStart(2, "0")}:00
                              </span>
                            </div>
                          );
                        }
                      )}

                      <div className="relative">
                        {/* Events */}
                        {items.map((item, index) => {
                          const start = new Date(item.start_time!);
                          const end = new Date(item.end_time!);
                          const startMinutes =
                            start.getHours() * 60 + start.getMinutes();
                          const durationMinutes = differenceInMinutes(
                            end,
                            start
                          );
                          const topPx = startMinutes - timelineOffset;
                          const height = Math.max(
                            durationMinutes * pxPerMinute,
                            minBoxHeight
                          );
                          const col = columnMap.get(item.id);
                          const width = 100 / (col?.total ?? 1);
                          const left = (col?.index ?? 0) * width;

                          return (
                            <div
                              key={item.id}
                              ref={(el) => {
                                if (el)
                                  itemRefs.current[`${dayKey}-${item.id}`] = el;
                              }}
                              className={`absolute box-border text-white text-xs px-3 py-1 rounded-lg shadow-md cursor-pointer transition hover:brightness-110 ${
                                categoryColors[item.category] || "bg-gray-400"
                              }`}
                              style={{
                                top: `${topPx}px`,
                                height: `${height}px`,
                                left: `calc(${left}% + 4px)`,
                                width: `calc(${width}% - 8px)`,
                              }}
                              onClick={() => {
                                /*
                              // Modal ist nicht mehr klickbar
                                if (!isOrganizer()) return;
                                setSelectedItem(item);
                                setTitle(item.title);
                                setDescription(item.description);
                                setCategory(item.category);
                                setStartTime(
                                  format(
                                    new Date(item.start_time),
                                    "yyyy-MM-dd'T'HH:mm"
                                  )
                                );
                                setEndTime(
                                  format(
                                    new Date(item.end_time),
                                    "yyyy-MM-dd'T'HH:mm"
                                  )
                                );
                                setModalOpen(true);
                                */
                              }}
                            >
                              <div className="font-semibold text-[13px] leading-snug truncate">
                                {item.title}
                              </div>
                              {isOrganizer() && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedItem(item);
                                    setTitle(item.title);
                                    setDescription(item.description);
                                    setCategory(item.category);
                                    setStartTime(
                                      item.start_time
                                        ? format(new Date(item.start_time), "yyyy-MM-dd'T'HH:mm")
                                        : ""
                                    );
                                    setEndTime(
                                      item.end_time
                                        ? format(new Date(item.end_time), "yyyy-MM-dd'T'HH:mm")
                                        : ""
                                    );
                                    setLocationText(item.location || "");
                                    setLocationCoords(
                                      item.location_lat && item.location_lng
                                        ? {
                                            lat: item.location_lat,
                                            lng: item.location_lng,
                                          }
                                        : null
                                    );
                                    setModalOpen(true);
                                  }}
                                  className="absolute top-1 right-1 text-white  hover:bg-white/50 rounded-full p-1"
                                  title="Bearbeiten"
                                >
                                  ⚙️
                                </button>
                              )}

                              <div className="text-[11px]">
                                {format(start, "HH:mm")} –{" "}
                                {format(end, "HH:mm")}
                              </div>
                              {item.description && (
                                <div className="text-[10px] italic mt-0.5 line-clamp-2">
                                  {item.description}
                                </div>
                              )}
                              <div className="mt-1">
                                <div className="text-[9px] font-medium bg-white/30 inline-block px-2 py-0.5 rounded">
                                  {item.category}
                                </div>

                                {(item.location || item.location_lat) && (
                                  <a
                                    href={getGoogleMapsUrl(item) ?? "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] mt-1 block text-white/90 hover:underline truncate"
                                  >
                                    📍 {item.location || "Ort auf Karte"}
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-400 italic">
                      💤 Für diesen Tag ist nichts geplant.
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">Lade Reise...</div>;
  if (!trip)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-red-400">Reise nicht gefunden</div>
    );

  const inviteLink = `${window.location.origin}/join/${trip.join_code}`;

  const categoryColors: Record<string, string> = {
    Reisen: "bg-gradient-to-br from-blue-400 to-blue-600",
    Übernachtung: "bg-gradient-to-br from-yellow-500 to-yellow-700",
    Verpflegung: "bg-gradient-to-br from-emerald-400 to-emerald-600",
    Aktivitäten: "bg-gradient-to-br from-orange-400 to-orange-600",
    Workshops: "bg-gradient-to-br from-pink-400 to-pink-600",
    Sonstiges: "bg-gradient-to-br from-gray-400 to-gray-600",
  };

  const participantsForTodoModal: Participant[] = participants
    .filter((p) => !p.is_blocked) // ❗ blockierte herausfiltern
    .map((p) => ({
      user_id: p.user_id,
      first_name: p.first_name,
      last_name: p.last_name,
      email: p.email,
    }));

  return (
    <div className="min-h-screen bg-[#0d1117]">
    <div className="max-w-3xl mx-auto pt-20">
      <TripTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        children={{
          overview: (
            <>
              <div
                className={`relative w-full h-72 overflow-hidden group ${
                  isOrganizer() ? "cursor-pointer" : ""
                }`}
                onClick={() => {
                  if (isOrganizer()) navigate(`/trip/${trip.id}/edit`);
                }}
              >
                {trip.cover_image_url ? (
                  <img
                    src={trip.cover_image_url}
                    alt={trip.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-sky-900/40 to-slate-800 flex items-center justify-center">
                    <span className="text-slate-500 text-sm">
                      {isOrganizer() ? "Tippe hier um ein Bild hinzuzufügen" : ""}
                    </span>
                  </div>
                )}

                {/* Gradient-Overlay von unten */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-[#0d1117]/50 to-transparent pointer-events-none" />

                {/* Overlay-Text */}
                <div className="absolute inset-0 px-5 pb-5 flex flex-col justify-end z-10 pointer-events-none">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-slate-400 bg-black/40 px-2 py-0.5 rounded-full">
                          {trip.destination}
                        </span>
                        <span className="text-[10px] text-slate-400 bg-black/40 px-2 py-0.5 rounded-full">
                          {trip.is_public ? "🔓 Öffentlich" : "🔒 Privat"}
                        </span>
                        {trip.max_participants && (
                          <span className="text-[10px] text-slate-400 bg-black/40 px-2 py-0.5 rounded-full">
                            Max. {trip.max_participants} Pers.
                          </span>
                        )}
                      </div>
                      <h1 className="text-2xl font-bold text-white drop-shadow-lg">{trip.name}</h1>
                      {trip.description && (
                        <p className="text-slate-300 text-sm mt-1 line-clamp-2 drop-shadow">{trip.description}</p>
                      )}
                    </div>
                    {isOrganizer() && (
                      <span className="z-20 pointer-events-none text-white text-lg mb-0.5">⚙️</span>
                    )}
                  </div>
                </div>
              </div>

               {isOrganizer() && (
                <div className="mt-4 px-5">
                  <button
                    onClick={() => {
                      const shareUrl = `${window.location.origin}/join/${trip.join_code}`;
                      const startDate = new Date(
                        trip.start_date
                      ).toLocaleDateString("de-CH");
                      const endDate = new Date(
                        trip.end_date
                      ).toLocaleDateString("de-CH");
                      const shareText = `🌍 Hey, komm doch mit auf meine Reise: "${trip.name}" nach ${trip.destination} vom ${startDate} bis ${endDate}!\n\nEinfach Link anklicken:\n${shareUrl}`;

                      if (navigator.share) {
                        navigator
                          .share({
                            title: "Einladung zur Reise",
                            text: `Hey, komm doch mit auf meine Reise: "${trip.name}" nach ${trip.destination} vom ${startDate} bis ${endDate}!`,
                            url: shareUrl,
                          })

                          .catch((err) => {
                            console.warn(
                              "Teilen abgebrochen oder nicht möglich",
                              err
                            );
                          });
                      } else {
                        navigator.clipboard.writeText(shareText);
                        window.prompt(
                          "Einladungslink wurde in die Zwischenablage kopiert. Du kannst ihn auch manuell weitergeben:",
                          shareText
                        );
                      }
                    }}
                    className="inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    Einladungslink der Reise teilen
                  </button>
                </div>
              )}

              {/*QUIZ------------------------------TEMP BUTTON FüR ORGA ZUM NEUE FRAGE GENERIEREN------------------------------------*/}
              {/*
              {isOrganizer() && quizFeatureActive && (
                <div className="mt-2">
                  <button
                    onClick={async () => {
                      const { error } = await supabase.functions.invoke(
                        "generate-quiz-for-trip",
                        {
                          body: {
                            trip_id: trip!.id,
                            user_id: user!.id,
                            ignore_interval: false, // 💥 Sperre ignorieren
                          },
                        }
                      );

                      if (error) {
                        alert("Fehler beim Generieren der Frage.");
                      } else {
                        alert("✅ Neue Frage generiert (Sperre ignoriert)");
                      }

                      await fetchTrip(); // neu laden, falls du Quizanzeige damit refreshst
                    }}
                    className="text-sm px-3 py-1 bg-yellow-100 hover:bg-yellow-200 border rounded"
                  >
                    ⚠️ Test: Neue Frage erzwingen
                  </button>
                </div>
              )}
                */}
              {/*QUIZ------------------------------TEMP BUTTON FüR ORGA ZUM NEUE FRAGE GENERIEREN------------------------------------*/}

              {isOrganizer() && quizFeatureActive && (
                <div className="mt-4 px-5 flex items-center gap-3">
                  {/* Label links */}
                  <label
                    htmlFor="quiz-switch"
                    className={`text-sm font-medium ${
                      pendingQuizEnabled ?? trip?.quiz_enabled
                        ? "text-emerald-400"
                        : "text-slate-400"
                    }`}
                  >
                    {pendingQuizEnabled ?? trip?.quiz_enabled
                      ? "🎯 Quiz aktiviert"
                      : "🎯 Quiz aktivieren"}
                  </label>

                  {/* Checkbox */}
                  <input
                    id="quiz-switch"
                    type="checkbox"
                    disabled={updatingQuiz}
                    checked={trip?.quiz_enabled ?? false}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      setPendingQuizEnabled(enabled);
                      setUpdatingQuiz(true);

                      const { error } = await supabase
                        .from("trips")
                        .update({ quiz_enabled: enabled })
                        .eq("id", trip.id);

                      if (!error && enabled) {
                        const { error: quizError } =
                          await supabase.functions.invoke(
                            "generate-quiz-for-trip",
                            {
                              body: {
                                trip_id: trip.id,
                                user_id: user!.id,
                              },
                            }
                          );

                        if (quizError) {
                          console.error(
                            "❌ Fehler beim Generieren der Quizfrage",
                            quizError
                          );
                        }
                      }

                      await fetchTrip();
                      setUpdatingQuiz(false);
                      setPendingQuizEnabled(null);
                    }}
                  />

                  {/* Hinweis rechts */}
                  {updatingQuiz && (
                    <span className="text-xs text-gray-500 animate-pulse">
                      {pendingQuizEnabled === true
                        ? "Quiz wird aktiviert…"
                        : "Quiz wird deaktiviert…"}
                    </span>
                  )}
                </div>
              )}

             

              {/* Phase-Kontext-Karte + Aktivitäts-Feed */}
              <div className="px-4 sm:px-5 mt-4 space-y-4">
                <PhaseContextCard trip={trip} programItems={programItems} isOrganizer={isOrganizer()} />
                <ActivityFeed tripId={trip.id} />
                {trip.quiz_enabled && user && (
                  <CurrentQuiz tripId={trip.id} userId={user.id} tripTitle={trip.name} />
                )}
              </div>

              

              <div className="px-5 mt-4">
                {isOrganizer() && (
                  <AddTodoModal
                    open={open}
                    onClose={closeModal}
                    tripId={trip.id}
                    participants={participantsForTodoModal}
                  />
                )}

                <TodoList
                  tripId={trip.id}
                  viewerId={user?.id ?? ""}
                  isOrganizer={isOrganizer()}
                />
              </div>
              <div className="px-5 mt-10">
                <TripSuggestions
                  tripId={trip.id}
                  userId={user!.id}
                  isOrganizer={isOrganizer()}
                  tripStartDate={trip.start_date}
                  tripEndDate={trip.end_date}
                  showSuccess={suggestionSaved}
                  onClearSuccess={() => setSuggestionSaved(false)}
                  onProgramItemCreated={() => loadProgramItems(trip.id)}
                />
              </div>

              <FloatingActionButton
                visible={activeTab === "overview"}
                localStorageKey="seen_fab_overview"
                actions={[
                  ...(isOrganizer()
                    ? [
                        {
                          label: "📝 Aufgabe hinzufügen",
                          onClick: openModal,
                        },
                        {
                          label: "📣 Neue Benachrichtigung",
                          onClick: () => setShowNotificationModal(true), // NEU
                        },
                      ]
                    : []),
                  {
                    label: "💡 Vorschlag einreichen",
                    onClick: () => setSuggestionModalOpen(true), // KORREKT
                  },
                ]}
              />
              {showNotificationModal && (
                <CreateNotificationModal
                  tripId={trip.id}
                  onClose={() => setShowNotificationModal(false)}
                />
              )}
              {suggestionModalOpen && (
                <NewSuggestionModal
                  tripId={trip.id}
                  userId={user!.id}
                  onClose={() => {
                    setSuggestionModalOpen(false);
                    setSuggestionSaved(true); // 🎯
                    setTimeout(() => setSuggestionSaved(false), 2000);
                  }}
                />
              )}
            </>
          ),
          program: (
            <>
              <VerticalTimeline
                items={programItems}
                trip={trip}
                isOrganizer={isOrganizer()}
                onEditItem={(item) => {
                  setSelectedItem(item);
                  setTitle(item.title);
                  setDescription(item.description);
                  setCategory(item.category);
                  if (item.start_time && item.end_time) {
                    setSchedulingMode("datetime");
                    setScheduledDate(item.scheduled_date || item.start_time.slice(0, 10));
                    setStartTime(format(new Date(item.start_time), "yyyy-MM-dd'T'HH:mm"));
                    setEndTime(format(new Date(item.end_time), "yyyy-MM-dd'T'HH:mm"));
                  } else if (item.scheduled_date) {
                    setSchedulingMode("date");
                    setScheduledDate(item.scheduled_date);
                    setStartTime("");
                    setEndTime("");
                  } else {
                    setSchedulingMode("none");
                    setScheduledDate("");
                    setStartTime("");
                    setEndTime("");
                  }
                  setLocationText(item.location || "");
                  setLocationCoords(
                    item.location_lat && item.location_lng
                      ? { lat: item.location_lat, lng: item.location_lng }
                      : null
                  );
                  setModalOpen(true);
                }}
                expandedDays={expandedDays}
                setExpandedDays={setExpandedDays}
                onMoveItem={isOrganizer() ? handleMoveItem : undefined}
              />

              {/* ➕ Floating Button */}
              {isOrganizer() && (
                <FloatingActionButton
                  visible={activeTab === "program"}
                  localStorageKey="seen_fab_program"
                  actions={[
                    {
                      label: "Programmpunkt hinzufügen",
                      onClick: () => {
                        setSelectedItem(null);
                        setTitle("");
                        setDescription("");
                        setCategory("Reisen");
                        setSchedulingMode("datetime");
                        if (trip) {
                          const start = new Date(trip.start_date + "T08:00");
                          const end = new Date(
                            start.getTime() + 60 * 60 * 1000
                          );
                          setScheduledDate(trip.start_date);
                          setStartTime(start.toISOString().slice(0, 16));
                          setEndTime(end.toISOString().slice(0, 16));
                        }
                        setModalOpen(true);
                      },
                    },
                    ...(aiPlanningActive ? [
                      {
                        label: "KI Aktivitäten planen",
                        onClick: () => setShowAiPlanningModal(true),
                      },
                    ] : []),
                  ]}
                />
              )}
            </>
          ),

          map: (
            <TripMap
              programItems={programItems}
              tripDestination={trip.destination}
            />
          ),

          participants: (
            <div className="px-4 sm:px-0">
              {participants.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">Teilnehmer</h2>
                  {participants.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">
                      Noch keine Teilnehmer vorhanden.
                    </p>
                  ) : (
                    <div>
                      {participants
                        .filter((p) => !p.is_blocked)
                        .map((p) => (
                          <ParticipantCard
                            key={p.user_id}
                            participant={p}
                            expanded={expandedParticipantId === p.user_id}
                            onClick={() =>
                              setExpandedParticipantId((prev) =>
                                prev === p.user_id ? null : p.user_id
                              )
                            }
                            viewerId={user!.id}
                            tripIsPublic={trip.is_public}
                            tripCreatorId={trip.creator_id}
                            onToggleOrganizer={async (userId, newStatus) => {
                              await supabase
                                .from("trip_participants")
                                .update({ is_co_organizer: newStatus })
                                .eq("trip_id", trip.id)
                                .eq("user_id", userId);
                              refreshParticipants();
                            }}
                            onToggleBlock={async (userId, newStatus) => {
                              await supabase
                                .from("trip_participants")
                                .update({ is_blocked: newStatus })
                                .eq("trip_id", trip.id)
                                .eq("user_id", userId);
                              refreshParticipants();
                            }}
                            isViewerOrganizer={isOrganizer()}
                            isBlocked={p.is_blocked ?? false}
                          />
                        ))}
                      

                      {/* 
                      // Form für Einladung per E-Mail
                      {isOrganizer() && (
                        <InviteUserForm
                          tripId={trip.id}
                          onSuccess={() => refreshParticipants()}
                        />
                      )}
                        */}

                      {isOrganizer() &&
                        (pendingCount > 0 || blockedCount > 0) && (
                          <div className="mt-6 bg-[#161b27] border border-[#2a3347] rounded-xl overflow-hidden">
                            <button
                              onClick={() =>
                                setShowModerationPanel((prev) => !prev)
                              }
                              className="w-full flex justify-between items-center px-4 py-3 bg-[#1e2639] text-sm font-medium text-slate-300 hover:text-slate-100 transition-colors"
                            >
                              <span>
                                🛠️{" "}
                                {showModerationPanel
                                  ? "Moderation ausblenden"
                                  : `Moderation: ${pendingCount} offen, ${blockedCount} blockiert`}
                              </span>
                              <span className="text-slate-500">{showModerationPanel ? "▲" : "▼"}</span>
                            </button>

                            {showModerationPanel && (
                              <div className="p-4 space-y-6">
                                {pendingCount > 0 && (
                                  <div>
                                    <h3 className="text-sm text-slate-400 font-medium mb-2">
                                      🕓 Offene Teilnahme-Anfragen
                                    </h3>
                                    <ul className="space-y-3">
                                      {pendingRequests.map((req) => (
                                        <ParticipantCard
                                          key={req.user_id}
                                          participant={req}
                                          expanded={false}
                                          onClick={() => {}}
                                          viewerId={user!.id}
                                          tripIsPublic={trip.is_public}
                                          tripCreatorId={trip.creator_id}
                                          isViewerOrganizer={true}
                                          isBlocked={false}
                                          hideExpandIcon={true}
                                          actionButtons={
                                            <>
                                              <button
                                                onClick={() =>
                                                  handleRequestAction(
                                                    req.user_id,
                                                    "accepted"
                                                  )
                                                }
                                                className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-emerald-500/20 transition-colors"
                                              >
                                                ✅ Annehmen
                                              </button>
                                              <button
                                                onClick={() =>
                                                  handleRequestAction(
                                                    req.user_id,
                                                    "rejected"
                                                  )
                                                }
                                                className="bg-red-950/40 text-red-400 border border-red-800 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-red-900/40 transition-colors"
                                              >
                                                ❌ Ablehnen
                                              </button>
                                            </>
                                          }
                                        />
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {blockedCount > 0 && (
                                  <div>
                                    <h3 className="text-sm text-slate-400 font-medium mb-2">
                                      🚫 Geblockte Teilnehmer
                                    </h3>
                                    {participants
                                      .filter((p) => p.is_blocked)
                                      .map((p) => (
                                        <ParticipantCard
                                          key={p.user_id}
                                          participant={p}
                                          expanded={
                                            expandedParticipantId === p.user_id
                                          }
                                          onClick={() =>
                                            setExpandedParticipantId((prev) =>
                                              prev === p.user_id
                                                ? null
                                                : p.user_id
                                            )
                                          }
                                          viewerId={user!.id}
                                          tripIsPublic={trip.is_public}
                                          tripCreatorId={trip.creator_id}
                                          onToggleOrganizer={async (
                                            userId,
                                            newStatus
                                          ) => {
                                            await supabase
                                              .from("trip_participants")
                                              .update({
                                                is_co_organizer: newStatus,
                                              })
                                              .eq("trip_id", trip.id)
                                              .eq("user_id", userId);
                                            refreshParticipants();
                                          }}
                                          onToggleBlock={async (
                                            userId,
                                            newStatus
                                          ) => {
                                            await supabase
                                              .from("trip_participants")
                                              .update({ is_blocked: newStatus })
                                              .eq("trip_id", trip.id)
                                              .eq("user_id", userId);
                                            refreshParticipants();
                                          }}
                                          isViewerOrganizer={true}
                                          isBlocked={true}
                                        />
                                      ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}
              {virtualParticipants.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-lg font-semibold mb-4">
                    Virtuelle Teilnehmer
                  </h2>

                  {virtualParticipants.map((p) => (
                    <ParticipantCard
                      key={p.virtual_profile_id}
                      participant={p}
                      expanded={expandedParticipantId === p.virtual_profile_id}
                      onClick={() =>
                        setExpandedParticipantId((prev) =>
                          prev === p.virtual_profile_id
                            ? null
                            : p.virtual_profile_id
                        )
                      }
                      viewerId={user!.id}
                      tripIsPublic={trip.is_public}
                      tripCreatorId={trip.creator_id}
                      isViewerOrganizer={false}
                      isBlocked={false}
                      hideExpandIcon={false}
                      //avatarUrlOverride="/avatars/generic-user-icon.png" // falls du sowas nutzt
                      actionButtons={
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();

                            const confirmed = await showConfirm({
                              title: "Virtuellen Teilnehmer entfernen",
                              message: `Möchtest du ${p.first_name ?? ""} ${
                                p.last_name ?? ""
                              } wirklich aus der Reise entfernen?`,
                              confirmLabel: "Entfernen",
                              cancelLabel: "Abbrechen",
                              icon: "warning",
                            });

                            if (!confirmed) return;

                            const { error } = await supabase
                              .from("trip_participants")
                              .delete()
                              .eq("trip_id", trip.id)
                              .eq("virtual_profile_id", p.virtual_profile_id);

                            if (error) {
                              console.error("Fehler beim Löschen:", error);
                            } else {
                              console.log("✅ Virtueller Teilnehmer entfernt");
                              refreshParticipants();
                            }
                          }}
                          className="text-red-600 hover:underline"
                        >
                          🗑️ Entfernen
                        </button>
                      }
                    />
                  ))}
                </div>
              )}
              {isOrganizer() && canAddVirtual && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowAddVirtualModal(true)}
                    className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    ➕ Virtuellen Teilnehmer hinzufügen
                  </button>
                </div>
              )}
            </div>
          ),
          accounting: (
            <div className="px-4 sm:px-0">
              {trip && (
                <ExpenseList
                  tripId={trip.id}
                  refreshTrigger={refreshExpenses}
                  onChange={() => setRefreshExpenses((prev) => prev + 1)}
                  isOrganizer={isOrganizer()}
                  openExpenseModal={showAddExpenseModal}
                  openPaymentModal={showAddPaymentModal}
                  participants={participants.filter((p) => !p.is_blocked)}
                  onCloseModals={() => {
                    navigate(`/trip/${trip.id}?tab=accounting`, {
                      replace: true,
                    });
                  }}
                  onNewExpense={isOrganizer() ? () => setShowExpenseBottomSheet(true) : undefined}
                  hideInternalFab={true}
                />
              )}

              {trip && (
                <ExpenseSummary
                  tripId={trip.id}
                  refreshTrigger={refreshExpenses}
                  onPay={isOrganizer() ? (userId, amount) => setPaymentPrefill({ userId, amount }) : undefined}
                />
              )}

              {isOrganizer() && (
                <FloatingActionButton
                  visible={activeTab === "accounting"}
                  localStorageKey="seen_fab_accounting_tripdetail"
                  actions={[
                    {
                      label: "💰 Ausgabe erfassen",
                      onClick: () => setShowExpenseBottomSheet(true),
                    },
                    {
                      label: "💳 Zahlung erfassen",
                      onClick: () => navigate(`/trip/${trip!.id}?tab=accounting&add=payment`),
                    },
                  ]}
                />
              )}
            </div>
          ),
          documents: (
            <div className="px-4 sm:px-0">
            <TripDocuments
              tripId={trip.id}
              userId={user!.id}
              tripCreatorId={trip.creator_id}
              programItems={programItems}
              isOrganizer={isOrganizer()}
            />
            </div>
          ),
          tasks: (
            <div className="mt-6 px-4 sm:px-0">
              <TodoStatusPanel tripId={trip.id} />
            </div>
          ),
          packlist: (
            <div className="px-4 sm:px-0">
              <UserPacklistTab tripId={trip.id} userId={user!.id} />
            </div>
          ),
        }}
        isOrganizer={isOrganizer()}
      />

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl p-6 w-full max-w-md relative shadow-xl">
            <button
              onClick={() => {
                setModalOpen(false);
                setSelectedItem(null);
              }}
              className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 text-xl transition-colors"
              aria-label="Modal schließen"
            >
              &times;
            </button>

            <h2 className="text-lg font-semibold text-slate-100 mb-4">
              {selectedItem
                ? "Programmpunkt bearbeiten"
                : "Neuer Programmpunkt"}
            </h2>

            <input
              type="text"
              placeholder="Titel"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600 mb-2"
            />
            {errors.title && (
              <p className="text-sm text-red-400 mb-2">{errors.title}</p>
            )}

            <textarea
              placeholder="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600 mb-2"
            />
            {errors.description && (
              <p className="text-sm text-red-400 mb-2">{errors.description}</p>
            )}

            <DestinationSearchInput
              value={locationText}
              onChange={(val) => setLocationText(val)}
              onCoordinates={(lat, lng) => setLocationCoords({ lat, lng })}
            />

            <select
              className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded outline-none focus:border-sky-400 mb-2"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="Reisen">Reisen</option>
              <option value="Übernachtung">Übernachtung</option>
              <option value="Verpflegung">Verpflegung</option>
              <option value="Aktivitäten">Aktivitäten</option>
              <option value="Workshops">Workshops</option>
              <option value="Sonstiges">Sonstiges</option>
            </select>

            {/* Terminplanung */}
            <div className="mb-3">
              <p className="text-sm text-slate-400 font-medium mb-2">Terminplanung</p>
              <div className="space-y-1.5">
                {(
                  [
                    { value: "none", label: "Kein Datum – noch offen" },
                    { value: "date", label: "Nur Datum" },
                    { value: "datetime", label: "Datum und Uhrzeit" },
                  ] as { value: "none" | "date" | "datetime"; label: string }[]
                ).map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={schedulingMode === value}
                      onChange={() => setSchedulingMode(value)}
                      className="accent-sky-500"
                    />
                    <span className="text-sm text-slate-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {(schedulingMode === "date" || schedulingMode === "datetime") && (
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={trip?.start_date}
                max={trip?.end_date}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 mb-2"
              />
            )}

            {schedulingMode === "datetime" && (
              <>
                <input
                  type="time"
                  value={startTime.includes("T") ? startTime.slice(11, 16) : startTime}
                  onChange={(e) => setStartTime(scheduledDate ? `${scheduledDate}T${e.target.value}` : e.target.value)}
                  className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 mb-2"
                  placeholder="Startzeit"
                />
                {errors.timeOrder && (
                  <p className="text-sm text-red-400 mb-2">{errors.timeOrder}</p>
                )}

                <input
                  type="time"
                  value={endTime.includes("T") ? endTime.slice(11, 16) : endTime}
                  onChange={(e) => setEndTime(scheduledDate ? `${scheduledDate}T${e.target.value}` : e.target.value)}
                  className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 mb-4"
                  placeholder="Endzeit"
                />
              </>
            )}
            {errors.time && (
              <p className="text-sm text-red-400 mb-2">{errors.time}</p>
            )}
            {errors.timeOrder && (
              <p className="text-sm text-red-400 mb-2">{errors.timeOrder}</p>
            )}
            {pollFeatureActive && (
              <label className="flex items-center gap-2 mb-4 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasPollOptions}
                  onChange={(e) => setHasPollOptions(e.target.checked)}
                  className="w-4 h-4 accent-sky-500"
                />
                Optionen (Umfrage) hinzufügen
              </label>
            )}

            <div className="flex justify-between items-center gap-2 mt-2">
              {selectedItem && (
                <button
                  onClick={handleDelete}
                  className="text-red-400 text-sm hover:text-red-300 transition-colors"
                >
                  Löschen
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setSelectedItem(null);
                  }}
                  className="bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] border border-[#2a3347] px-4 py-2 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {!modalOpen && pollEditorOpen && newProgramItemId && (
        <PollEditorModal
          programItemId={newProgramItemId}
          onClose={() => {
            setPollEditorOpen(false);
            setNewProgramItemId(null);
          }}
        />
      )}
      {showAddVirtualModal && (
        <AddVirtualParticipantModal
          tripId={trip.id}
          onClose={() => setShowAddVirtualModal(false)}
          onSuccess={refreshParticipants}
        />
      )}
      {showAiPlanningModal && trip && user && (
        <AiTripPlanningModal
          mode="activities"
          trip={{
            id: trip.id,
            start_date: trip.start_date,
            end_date: trip.end_date,
            destination: trip.destination,
          }}
          userId={user.id}
          onClose={(created) => {
            setShowAiPlanningModal(false);
            if (created) loadProgramItems(trip.id);
          }}
        />
      )}

      {showExpenseBottomSheet && trip && (
        <NewExpenseBottomSheet
          tripId={trip.id}
          participants={participants.filter((p) => !p.is_blocked)}
          onSaved={() => {
            setShowExpenseBottomSheet(false);
            setRefreshExpenses((prev) => prev + 1);
          }}
          onClose={() => setShowExpenseBottomSheet(false)}
        />
      )}

      {paymentPrefill && trip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl p-6 w-full max-w-md relative shadow-xl">
            <button
              onClick={() => setPaymentPrefill(null)}
              className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 text-xl transition-colors"
              aria-label="Modal schließen"
            >
              &times;
            </button>
            <h2 className="text-sm font-semibold text-slate-100 mb-5">Zahlung erfassen</h2>
            <PaymentForm
              tripId={trip.id}
              participants={participants.filter((p) => !p.is_blocked)}
              initialUserId={paymentPrefill.userId}
              initialAmount={paymentPrefill.amount.toFixed(2)}
              isOrganizer={true}
              onSaved={() => {
                setPaymentPrefill(null);
                setRefreshExpenses((prev) => prev + 1);
              }}
              onCancel={() => setPaymentPrefill(null)}
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
